// frontend/src/components/public/blocks/UpcomingPerformancesBlock.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  MusicNote as MusicIcon,
  ConfirmationNumber as TicketIcon
} from '@mui/icons-material';
import axios from 'axios';

interface PerformanceData {
  _id: string;
  artistName: string;
  startTime: string;
  endTime: string;
  genre: string;
  ticketPrice: number;
  isFree: boolean;
  imageUrl: string | null;
}

interface UpcomingPerformancesBlockProps {
  data: {
    sectionTitle: string;
    ctaText: string;
  };
  theme?: {
    primaryColor: string;
    accentColor: string;
  };
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const UpcomingPerformancesBlock: React.FC<UpcomingPerformancesBlockProps> = ({ data, theme }) => {
  const [performances, setPerformances] = useState<PerformanceData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getDaysUntil = (dateString: string) => {
  const now = new Date();
  const eventDate = new Date(dateString);
  const diffTime = eventDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays < 7) return `In ${diffDays} days`;
  if (diffDays < 14) return 'In 1 week';
  if (diffDays < 21) return 'In 2 weeks';
  if (diffDays < 28) return 'In 3 weeks';
  return `In ${Math.ceil(diffDays / 7)} weeks`;
};

  useEffect(() => {
    const fetchPerformances = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await axios.get<PerformanceData[]>(
          `${API_BASE_URL}/api/performances/public/upcoming`
        );
        
        setPerformances(response.data);
      } catch (err: any) {
        console.error('Error fetching upcoming performances:', err);
        setError(err.response?.data?.message || 'Failed to load performances');
      } finally {
        setLoading(false);
      }
    };

    fetchPerformances();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const generateMailtoLink = (performance: PerformanceData) => {
    const subject = encodeURIComponent(`Portfolio Invitation Request - ${performance.artistName}`);
    const body = encodeURIComponent(
      `Dear Improvised Music Company,\n\n` +
      `I'm interested in attending the following performance:\n\n` +
      `Artist: ${performance.artistName}\n` +
      `Date: ${formatDate(performance.startTime)}\n` +
      `Time: ${formatTime(performance.startTime)}\n\n` +
      `Performance ID: ${performance._id}\n\n` +
      `Please send me more information about attending this event.\n\n` +
      `Best regards`
    );
    
    return `mailto:ken@improvisedmusic.ie?subject=${subject}&body=${body}`;
  };

  if (loading) {
    return (
      <Box sx={{ py: 8, textAlign: 'center', bgcolor: '#f5f5f5' }}>
        <CircularProgress size={50} sx={{ color: theme?.primaryColor || '#3A9188' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 8, px: 3 }}>
        <Alert severity="warning">{error}</Alert>
      </Box>
    );
  }

  if (performances.length === 0) {
    return (
      <Box sx={{ py: 8, textAlign: 'center', bgcolor: '#f5f5f5' }}>
        <MusicIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
        <Typography variant="h6" sx={{ color: '#666' }}>
          No upcoming performances scheduled
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 8, px: 3, bgcolor: '#f9f9f9' }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
 <Typography
  variant="h3"
  sx={{
    fontWeight: 700,
    color: theme?.primaryColor || '#2C3E50',
    mb: 2,
    textAlign: 'center'
  }}
>
  Upcoming Performances
</Typography>

<Typography
  variant="subtitle1"
  sx={{
    color: '#555',
    mb: 5,
    textAlign: 'center',
    maxWidth: 700,
    mx: 'auto'
  }}
>
  You're invited — be our guest at an upcoming show!  
  Just click <strong>Request Invitation</strong> for a free ticket.
</Typography>

        <Grid container spacing={4}>
          {performances.map((performance) => (
            <Grid item xs={12} md={4} key={performance._id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                  }
                }}
              >
                {performance.imageUrl ? (
                  <CardMedia
                    component="img"
                    height="240"
                    image={performance.imageUrl}
                    alt={performance.artistName}
                    sx={{ objectFit: 'cover' }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 240,
                      bgcolor: theme?.primaryColor || '#3A9188',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <MusicIcon sx={{ fontSize: 80, color: 'white', opacity: 0.5 }} />
                  </Box>
                )}

                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      color: '#edf0f3ff',
                      mb: 2
                    }}
                  >
                    {performance.artistName}
                  </Typography>

                  {performance.genre && (
                    <Chip
                      label={performance.genre}
                      size="small"
                      sx={{
                        mb: 2,
                        bgcolor: theme?.accentColor || '#E8F5E9',
                        color: theme?.primaryColor || '#2E7D32',
                        fontWeight: 500
                      }}
                    />
                  )}

                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
  <CalendarIcon sx={{ fontSize: 18, color: '#fcfcfcff' }} />
  <Typography variant="h6" sx={{ color: '#ffffffff' }}>
    {formatDate(performance.startTime)}
  </Typography>
  <Chip
    label={getDaysUntil(performance.startTime)}
    size="small"
    sx={{
      ml: 1,
      bgcolor: '#ffffff22',
      color: '#fff',
      fontWeight: 500
    }}
  />
</Box>
                  

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CalendarIcon sx={{ fontSize: 18, color: '#fafafaff' }} />
                    <Typography variant="h6" sx={{ color: '#f8f3f3ff' }}>
                      {formatTime(performance.startTime)} - {formatTime(performance.endTime)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TicketIcon sx={{ fontSize: 18, color: '#fcfafaff' }} />
                    <Typography variant="h6" sx={{ color: '#ffffffff', fontWeight: 500 }}>
                      {performance.isFree ? 'Free Entry' : `€${performance.ticketPrice}`}
                    </Typography>
                  </Box>
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    href={generateMailtoLink(performance)}
                    sx={{
                      bgcolor: theme?.primaryColor || '#3A9188',
                      color: 'white',
                      fontWeight: 600,
                      py: 1.5,
                      '&:hover': {
                        bgcolor: theme?.accentColor || '#2E7D32'
                      }
                    }}
                  >
                    {data.ctaText}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default UpcomingPerformancesBlock;